import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Blog.css";

const PAGE_SIZE = 9;

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let mounted = true;

    async function loadPosts() {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          status: "published",
          page: String(page),
          pageSize: String(PAGE_SIZE),
        });
        if (category !== "All") params.set("category", category);

        const res = await fetch(`/.netlify/functions/get-posts?${params}`);
        const json = await res.json();
        if (!mounted) return;

        if (!res.ok || !json.ok) {
          setLoadError(json.error || "Failed to load posts.");
          setPosts([]);
          return;
        }

        setPosts(json.posts || []);
        setTotalPages(json.totalPages || 1);
        // Categories come from the full published set; keep the first load's list stable
        if (json.categories && (categories.length === 0 || category === "All")) {
          setCategories(json.categories);
        }
        setLoadError("");
      } catch (err) {
        if (!mounted) return;
        setLoadError("Network error while loading posts.");
        setPosts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadPosts();
    return () => {
      mounted = false;
    };
  }, [page, category]); // eslint-disable-line react-hooks/exhaustive-deps

  function selectCategory(cat) {
    setCategory(cat);
    setPage(1);
  }

  const isFirstDefaultPage = page === 1 && category === "All";
  const featured = isFirstDefaultPage ? posts[0] : null;
  const gridPosts = featured ? posts.slice(1) : posts;

  return (
    <main className="page page--contained blog-page" aria-labelledby="blog-heading">
      <header className="page-header page-header--flush">
        <h1 id="blog-heading" className="page-title">Blog</h1>
        <p className="page-subtitle">
          Reflections, news, and writing from Living Room Inquiries.
        </p>

        {categories.length > 0 && (
          <div className="blog-filter" role="group" aria-label="Filter by category">
            <button
              className={`blog-pill ${category === "All" ? "is-active" : ""}`}
              onClick={() => selectCategory("All")}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c}
                className={`blog-pill ${category === c ? "is-active" : ""}`}
                onClick={() => selectCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </header>

      {loading && <div className="page-status">Loading posts…</div>}
      {!loading && loadError && <div className="page-status">{loadError}</div>}

      {!loading && !loadError && posts.length === 0 && (
        <div className="page-status">No posts yet. Check back soon!</div>
      )}

      {!loading && !loadError && featured && (
        <Link to={`/blog/${featured.slug}`} className="blog-featured">
          {featured.coverImageUrl && (
            <div className="blog-featured-media">
              <img src={featured.coverImageUrl} alt="" loading="lazy" />
            </div>
          )}
          <div className="blog-featured-body">
            <span className="blog-featured-label">Latest</span>
            <h2 className="blog-featured-title">{featured.title}</h2>
            {featured.excerpt && (
              <p className="blog-featured-excerpt">{featured.excerpt}</p>
            )}
            <PostMeta post={featured} />
          </div>
        </Link>
      )}

      {!loading && !loadError && gridPosts.length > 0 && (
        <div className="blog-grid">
          {gridPosts.map((p) => (
            <PostCard key={p.id} post={p} />
          ))}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <nav className="blog-pagination" aria-label="Pagination">
          <button
            className="blog-page-btn"
            disabled={page <= 1}
            onClick={() => setPage((n) => Math.max(1, n - 1))}
          >
            ← Prev
          </button>
          <span className="blog-page-info">
            Page {page} of {totalPages}
          </span>
          <button
            className="blog-page-btn"
            disabled={page >= totalPages}
            onClick={() => setPage((n) => Math.min(totalPages, n + 1))}
          >
            Next →
          </button>
        </nav>
      )}
    </main>
  );
}

function PostCard({ post }) {
  return (
    <article className="blog-card">
      <Link to={`/blog/${post.slug}`} className="blog-card-link">
        {post.coverImageUrl && (
          <div className="blog-card-media">
            <img src={post.coverImageUrl} alt="" loading="lazy" />
          </div>
        )}
        <div className="blog-card-body">
          {post.category && <span className="blog-card-cat">{post.category}</span>}
          <h3 className="blog-card-title">{post.title}</h3>
          {post.excerpt && <p className="blog-card-excerpt">{post.excerpt}</p>}
          <PostMeta post={post} />
        </div>
      </Link>
    </article>
  );
}

function PostMeta({ post }) {
  const date = formatDate(post.publishedDate || post.createdAt);
  return (
    <p className="blog-meta">
      {post.author && <span>{post.author}</span>}
      {post.author && date && <span aria-hidden="true"> · </span>}
      {date && <span>{date}</span>}
    </p>
  );
}
